import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LoadingMessages = [
    "Initialisation des neurones de l'IA...",
    "Décodage de votre ADN professionnel...",
    "Analyse de vos super-pouvoirs...",
    "Alignement avec les opportunités mondiales...",
    "Polissage de votre profil...",
    "Vérification de la compatibilité culturelle...",
    "Succès imminent..."
];

const CircularProgress = ({ duration = 35000, onComplete, isLoading = true }) => {
    const [progress, setProgress] = useState(0);
    const [messageIndex, setMessageIndex] = useState(0);

    useEffect(() => {
        // Message rotation
        const messageInterval = setInterval(() => {
            setMessageIndex(prev => (prev + 1) % LoadingMessages.length);
        }, 1500);
        return () => clearInterval(messageInterval);
    }, []);

    useEffect(() => {
        const intervalTime = 50;

        const timer = setInterval(() => {
            setProgress(prev => {
                // If API is done (isLoading = false), accelerate to 100%
                if (!isLoading) {
                    const next = prev + 5; // Fast finish
                    if (next >= 100) {
                        clearInterval(timer);
                        if (onComplete) setTimeout(onComplete, 500);
                        return 100;
                    }
                    return next;
                }

                // While loading: Increment based on duration but stall at 95%
                if (prev >= 95) {
                    return 95; // Stall waiting for API
                }

                // Slow increment
                const step = 100 / (duration / intervalTime);
                return Math.min(prev + step, 95);
            });
        }, intervalTime);

        return () => clearInterval(timer);
    }, [duration, onComplete, isLoading]);

    const circumference = 2 * Math.PI * 45; // r=45
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
        <div className="flex flex-col items-center justify-center p-8">
            <div className="relative w-40 h-40 flex items-center justify-center">
                {/* Background Circle - Light Blue */}
                <svg className="absolute w-full h-full transform -rotate-90 overflow-visible">
                    <circle
                        cx="80"
                        cy="80"
                        r="54"
                        stroke="#dbeafe"
                        strokeWidth="16"
                        fill="transparent"
                        className="transition-colors duration-300"
                    />
                    {/* Progress Circle - Brand Blue with Glow */}
                    <circle
                        cx="80"
                        cy="80"
                        r="54"
                        stroke="#2563eb"
                        strokeWidth="16"
                        fill="transparent"
                        strokeDasharray={2 * Math.PI * 54}
                        strokeDashoffset={(2 * Math.PI * 54) - (progress / 100) * (2 * Math.PI * 54)}
                        strokeLinecap="round"
                        className="transition-[stroke-dashoffset] duration-200 ease-linear"
                        style={{
                            filter: `drop-shadow(0 0 ${6 + (progress / 20)}px rgba(37, 99, 235, ${0.4 + (progress / 300)}))`
                        }}
                    />
                </svg>
                {/* Percentage Text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center text-blue-900">
                    <span className="text-3xl font-black tracking-tighter">{Math.round(progress)}%</span>
                </div>
            </div>

            {/* Animated Text Messages */}
            <div className="h-8 mt-8 flex items-center justify-center">
                <AnimatePresence mode="wait">
                    <motion.p
                        key={messageIndex}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="text-blue-600/80 font-bold text-sm text-center"
                    >
                        {LoadingMessages[messageIndex]}
                    </motion.p>
                </AnimatePresence>
            </div>
        </div>
    );
};

export default CircularProgress;
